import {
  Box,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { searchReadingOrders } from "../../../services/searchApi";

const ReadingOrderAutocomplete = ({
  value,
  excludedIds = [],
  onSelectionChange,
  ariaLabel = "Reading order",
}) => {
  const [inputValue, setInputValue] = useState(value?.title || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchTerm = inputValue.trim();
  const excludedIdSet = new Set(excludedIds.map((id) => String(id)));

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setInputValue(value?.title || "");
  }, [value?.id, value?.title]);

  const { data, isLoading } = useQuery(
    ["publishedReadingOrderSearch", debouncedSearch],
    () => searchReadingOrders(debouncedSearch),
    {
      enabled: debouncedSearch.length >= 3,
      keepPreviousData: false,
    }
  );

  const suggestions = (data?.data || []).filter(
    (readingOrder) =>
      !excludedIdSet.has(String(readingOrder.id)) ||
      String(readingOrder.id) === String(value?.id)
  );

  const handleSelection = (readingOrder) => {
    setInputValue(readingOrder.title);
    setIsOpen(false);
    onSelectionChange(readingOrder);
  };

  return (
    <Box position="relative" w="full">
      <InputGroup>
        <Input
          aria-label={ariaLabel}
          value={inputValue}
          placeholder="Search all published reading orders"
          onFocus={() => setIsOpen(true)}
          onChange={(event) => {
            setInputValue(event.target.value);
            setIsOpen(true);
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        />
        {isLoading ? (
          <InputRightElement>
            <Spinner size="sm" />
          </InputRightElement>
        ) : null}
      </InputGroup>

      {isOpen && searchTerm.length >= 3 ? (
        <List
          role="listbox"
          position="absolute"
          top="full"
          left="0"
          right="0"
          zIndex="dropdown"
          mt="1"
          maxH="260px"
          overflowY="auto"
          bg="gray.800"
          borderWidth="1px"
          borderColor="gray.600"
          borderRadius="md"
        >
          {!isLoading && suggestions.length === 0 ? (
            <ListItem p="3">
              <Text color="gray.400">No reading orders found.</Text>
            </ListItem>
          ) : null}
          {suggestions.map((readingOrder) => (
            <ListItem
              key={readingOrder.id}
              role="option"
              aria-label={readingOrder.title}
              p="2"
              cursor="pointer"
              _hover={{ bg: "gray.700" }}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelection(readingOrder);
              }}
            >
              <Box display="flex" alignItems="center" gap="3">
                {readingOrder.thumbnail ? (
                  <Image
                    src={readingOrder.thumbnail}
                    alt=""
                    w="30px"
                    h="42px"
                    objectFit="cover"
                    borderRadius="sm"
                  />
                ) : null}
                <Text>{readingOrder.title}</Text>
              </Box>
            </ListItem>
          ))}
        </List>
      ) : null}
    </Box>
  );
};

export default ReadingOrderAutocomplete;
